import { create } from 'zustand';
import { fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';

const FZ = FIELDS.geoOpsZones;
const FCS = FIELDS.cameraSources;
const FGS = FIELDS.gisDataSources;


interface GeoZone { id: number; incidentId: number | null; name: string; zoneType: string; geometry: string; status: string; color: string; [k: string]: unknown; }
interface CameraSource { id: number; name: string; provider: string; baseUrl: string; status: string; [k: string]: unknown; }
interface GISSource { id: number; name: string; url: string; protocol: string; status: string; featureCount: number; [k: string]: unknown; }

interface GeoOpsState {
  zones: GeoZone[]; cameraSources: CameraSource[]; gisSources: GISSource[]; loading: boolean;
  fetchZones: () => Promise<void>; fetchCameraSources: () => Promise<void>; fetchGISSources: () => Promise<void>;
  createZone: (data: Record<string, unknown>) => Promise<number | null>;
  updateZone: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteZone: (id: number) => Promise<void>;
  createCameraSource: (data: Record<string, unknown>) => Promise<number | null>;
  createGISSource: (data: Record<string, unknown>) => Promise<number | null>;
  // V1 compat
  isLoading: boolean;
  loadZones: (_id?: any) => Promise<void>;
  saveZone: (data: any) => Promise<number | null>;
}

export const useGeoOpsStore = create<GeoOpsState>((set, get) => ({
  zones: [], cameraSources: [], gisSources: [], loading: false,
  fetchZones: async () => { set({ loading: true }); try { const r = await fetchRecords<GeoZone>(TABLES.geoOpsZones, { limit: 200 }); set({ zones: r.records }); } catch (e) { console.error('[Geo]', e); } set({ loading: false }); },
  fetchCameraSources: async () => { try { const r = await fetchRecords<CameraSource>(TABLES.cameraSources, { limit: 50 }); set({ cameraSources: r.records }); } catch (e) { console.error('[Geo]', e); } },
  fetchGISSources: async () => { try { const r = await fetchRecords<GISSource>(TABLES.gisDataSources, { limit: 50 }); set({ gisSources: r.records }); } catch (e) { console.error('[Geo]', e); } },
  createZone: async (data) => { try { const p: Record<string,unknown> = {};
      if (data.name) p[FZ.name] = data.name;
      if (data.zoneType) p[FZ.zoneType] = data.zoneType;
      if (data.geometry) p[FZ.geometry] = data.geometry;
      if (data.status) p[FZ.status] = data.status;
      if (data.color) p[FZ.color] = data.color;
      if (data.incidentId) p[FZ.incidentId] = data.incidentId;
      if (data.notes) p[FZ.notes] = data.notes;
      const r = await createRecord<GeoZone>(TABLES.geoOpsZones, p); await get().fetchZones(); return r.id; } catch (e) { console.error('[Geo]', e); return null; } },
  updateZone: async (id, data) => { try { const p2: Record<string,unknown> = {};
      if (data.name) p2[FZ.name] = data.name;
      if (data.status) p2[FZ.status] = data.status;
      if (data.notes) p2[FZ.notes] = data.notes;
      await updateRecord(TABLES.geoOpsZones, id, p2); await get().fetchZones(); } catch (e) { console.error('[Geo]', e); } },
  deleteZone: async (id) => { try { await deleteRecord(TABLES.geoOpsZones, id); await get().fetchZones(); } catch (e) { console.error('[Geo]', e); } },
  createCameraSource: async (data) => { try { const cp: Record<string,unknown> = {};
      if (data.name) cp[FCS.name] = data.name;
      if (data.provider) cp[FCS.provider] = data.provider;
      if (data.baseUrl) cp[FCS.baseUrl] = data.baseUrl;
      if (data.status) cp[FCS.status] = data.status;
      const r = await createRecord<CameraSource>(TABLES.cameraSources, cp); await get().fetchCameraSources(); return r.id; } catch (e) { console.error('[Geo]', e); return null; } },
  createGISSource: async (data) => { try { const gp: Record<string,unknown> = {};
      if (data.name) gp[FGS.name] = data.name;
      if (data.url) gp[FGS.url] = data.url;
      if (data.protocol) gp[FGS.protocol] = data.protocol;
      if (data.status) gp[FGS.status] = data.status;
      const r = await createRecord<GISSource>(TABLES.gisDataSources, gp); await get().fetchGISSources(); return r.id; } catch (e) { console.error('[Geo]', e); return null; } },

  // V1 compat aliases
  isLoading: false,
  loadZones: async (_id?: any) => { await (get() as any).fetchZones(); },
  saveZone: async (data: any) => { return (get() as any).createZone(data); },
}));
